import React from 'react'
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom'
import { RequireAuth } from './util/AuthContext'
import RequireLevel from './util/RequireLevel'

// Main component - keep as static import since it's always needed
import Main from './components/Main'
import PublicLayout from './components/PublicLayout'

// Import all page components directly (no lazy loading)
import ChangePassword from './components/pages/auth/ChangePassword'
import CustomerDeviceGroupAddPage from './components/pages/CustomerDeviceGroupAddPage'
import CustomerDeviceGroupEditPage from './components/pages/CustomerDeviceGroupEditPage'
import CustomerSettingsPage from './components/pages/CustomerSettingsPage'
import CustomerDeviceGroupsPage from './components/pages/CustomerDeviceGroupsPage'
import CustomerReportSettingsPage from './components/pages/CustomerReports/CustomerReportsSettingPage.tsx'
import CustomerReportSettingsEditPage from './components/pages/CustomerReports/CustomerReportSettingsEditPage.tsx'
import CustomerReportsSettingsGroupsPage from './components/pages/CustomerReports/CustomerReportSettingsGroupsPage'
import CustomerUsersPage from './components/pages/CustomerUsersPage'
import DeviceAdmin from './components/pages/DeviceAdmin'
import DeviceConfigure from './components/pages/DeviceConfigure'
import DeviceList from './components/pages/DeviceList'
import ReplaceModems from './components/pages/ReplaceModemPage'
import DeviceRules from './components/pages/DeviceRulesPage'
import DeviceSettings from './components/pages/DeviceSettings'
import DocPage from './components/pages/DocPage'
import DocViewPage from './components/pages/DocViewPage'
import DistributorToolsPage from './components/pages/DistributorToolsPage'
import TransferDevicesPage from './components/pages/TransferDevicesPage'
import NewCustomer from './components/pages/NewCustomer'
import ExistingCustomer from './components/pages/ExistingCustomer'
import GridViewModemReplacementPage from './components/pages/GridViewModemReplacementPage'
import Home from './components/pages/Home'
import Login from './components/pages/auth/Login'
import MoistureSensorPage from './components/pages/MoistureSensorPage'
import MoistureCombinedSettingsPage from './components/pages/MoistureCombinedSettingsPage'
import PumpPage from './components/pages/PumpPage'
import FlowPage from './components/pages/FlowPage'
import FlowInterfaceSettingsPage from './components/pages/FlowInterfaceSettingsPage'
import PumpInterfaceSettingsPage from './components/pages/PumpInterfaceSettingsPage'
import ValvePage from './components/pages/ValvePage'
import Rule from './components/pages/Rule'
import SchedulePage from './components/pages/SchedulePage'
import SettingPage from './components/pages/SettingPage'
import SettingHistoryPage from './components/pages/SettingHistoryPage'
import Signup from './components/pages/auth/Signup'
import SupportPage from './components/pages/SupportPage'
import Telemetry from './components/pages/Telemetry'
import Terms from './components/pages/Terms'
import UserPage from './components/pages/UserPage'
import UserGroupsPage from './components/pages/UserGroupsPage'
import UserEditPage from './components/pages/UserEditPage'
import UserInvite from './components/pages/UserInvite'
import UserPreferences from './components/pages/UserPreferences'
import ApiKeyPage from './components/pages/ApiKeyPage'
import ApiKeyCreatePage from './components/pages/ApiKeyCreatePage'
import DataDetailsPage from './components/pages/DataDetailsPage'
import ErrorBoundaryRouter from './ErrorBoundaryRouter'

const LegacyAppRedirect = () => {
  const location = useLocation()
  const nextPathname = location.pathname.replace(/^\/app(\/|$)/, '/')
  const normalizedPath = nextPathname === '' ? '/' : nextPathname
  const nextPath = `${normalizedPath}${location.search}${location.hash}`
  return <Navigate to={nextPath} replace />
}

// Define routes configuration for React Router v6
const routes = [
  {
    path: '/app/*',
    element: <LegacyAppRedirect />,
    ErrorBoundary: ErrorBoundaryRouter,
  },
  // Public routes - no authentication required, with PublicLayout wrapper
  {
    path: '/login',
    element: (
      <PublicLayout>
        <Login />
      </PublicLayout>
    ),
    ErrorBoundary: ErrorBoundaryRouter,
  },
  {
    path: '/signup',
    element: (
      <PublicLayout>
        <Signup />
      </PublicLayout>
    ),
    ErrorBoundary: ErrorBoundaryRouter,
  },
  {
    path: '/terms',
    element: (
      <PublicLayout>
        <Terms />
      </PublicLayout>
    ),
    ErrorBoundary: ErrorBoundaryRouter,
  },
  {
    path: '/user/password/new',
    element: (
      <PublicLayout>
        <ChangePassword />
      </PublicLayout>
    ),
    ErrorBoundary: ErrorBoundaryRouter,
  },

  // Protected routes - authentication required, with Main as parent layout
  {
    path: '/',
    element: (
      <RequireAuth>
        <Main />
      </RequireAuth>
    ),
    ErrorBoundary: ErrorBoundaryRouter,
    children: [
      // Home routes
      {
        index: true,
        element: <Home />,
      },
      {
        path: 'home',
        element: <Home />,
      },

      // Documentation routes
      {
        path: 'docs',
        element: <DocPage />,
      },
      {
        path: 'docs/:docID',
        element: <DocViewPage />,
      },
      {
        path: 'docs/:docID/:section',
        element: <DocViewPage />,
      },

      // Support and API routes
      {
        path: 'support/:customerID/apiKeys',
        element: <ApiKeyPage />,
      },
      {
        path: 'support/:customerID/createApi',
        element: <ApiKeyCreatePage />,
      },
      {
        path: 'settings',
        element: <CustomerSettingsPage />,
      },
      {
        path: 'distributor',
        element: <DistributorToolsPage />,
      },
      {
        path: 'support',
        element: <SupportPage />,
      },

      // User management routes
      {
        path: 'user',
        element: <UserPage />,
      },
      {
        path: 'user/invite',
        element: <UserInvite />,
      },
      {
        path: 'user/preferences/:preference',
        element: <UserPreferences />,
      },
      {
        path: 'welcome',
        element: <Signup />,
      },

      // Device management routes
      {
        path: ':customerID/devices',
        element: <DeviceList />,
      },
      {
        path: 'replaceModem/:customerID',
        element: <ReplaceModems />,
      },
      {
        path: ':customerID/devices/:deviceID',
        element: <DeviceConfigure />,
      },
      {
        path: ':customerID/devices/:deviceID/settings/modemReplacement',
        element: <GridViewModemReplacementPage />,
      },
      {
        path: ':customerID/devices/:deviceID/settings/dataDetails',
        element: <DataDetailsPage />,
      },
      {
        path: ':customerID/devices/:deviceID/settings',
        element: <DeviceSettings />,
      },
      {
        path: ':customerID/devices/:deviceID/admin',
        element: <DeviceAdmin />,
      },
      {
        path: ':customerID/devices/:deviceID/settings/schedule',
        element: <SchedulePage />,
      },
      {
        path: ':customerID/devices/:deviceID/settings/schedule/:scheduleNumber',
        element: <SchedulePage />,
      },
      {
        path: ':customerID/devices/:deviceID/settings/:setting',
        element: <SettingPage />,
      },
      {
        path: ':customerID/devices/:deviceID/settingHistory',
        element: <SettingHistoryPage />,
      },
      {
        path: ':customerID/devices/:deviceID/settings/:setting/:settingNo',
        element: <SettingPage />,
      },
      {
        path: ':customerID/devices/:deviceID/telemetry/:type',
        element: <Telemetry />,
      },
      {
        path: ':customerID/devices/:deviceID/rules',
        element: <DeviceRules />,
      },
      {
        path: ':customerID/devices/:deviceID/rules/:ruleID',
        element: <Rule />,
      },

      // Moisture sensor routes
      {
        path: ':customerID/moistureSensor/:deviceID',
        element: <MoistureSensorPage />,
      },
      {
        path: ':customerID/moistureSensor/:deviceID/settings/moistureCombined',
        element: <MoistureCombinedSettingsPage />,
      },

      // Pump and flow routes
      {
        path: ':customerID/pump/:deviceID',
        element: <PumpPage />,
      },
      {
        path: ':customerID/pump/:deviceID/settings',
        element: <PumpInterfaceSettingsPage />,
      },
      {
        path: ':customerID/flow/:deviceID/settings',
        element: <FlowInterfaceSettingsPage />,
      },
      {
        path: ':customerID/flow/:deviceID',
        element: <FlowPage />,
      },

      // Valve and pressure switch routes
      {
        path: ':customerID/valve/:deviceID',
        element: <ValvePage />,
      },
      {
        path: ':customerID/pressureSwitch/:deviceID',
        element: <ValvePage />,
      },

      // Device group routes
      {
        path: ':customerID/settings/deviceGroups',
        element: <CustomerDeviceGroupsPage />,
      },
      {
        path: ':customerID/settings/deviceGroups/add',
        element: <CustomerDeviceGroupAddPage />,
      },
      {
        path: 'groups/:groupID',
        element: <CustomerDeviceGroupEditPage />,
      },

      // Report settings routes (level 10 and above)
      {
        path: ':customerID/settings/reports',
        element: <RequireLevel minLevel={10}><CustomerReportSettingsPage /></RequireLevel>,
      },
      {
        path: ':customerID/settings/reports/add',
        element: <RequireLevel minLevel={10}><CustomerReportSettingsEditPage /></RequireLevel>,
      },
      {
        path: ':customerID/settings/reports/:ruleID',
        element: <RequireLevel minLevel={10}><CustomerReportSettingsEditPage /></RequireLevel>,
      },
      {
        path: ':customerID/settings/reports/:ruleID/groups',
        element: <RequireLevel minLevel={10}><CustomerReportsSettingsGroupsPage /></RequireLevel>,
      },

      // User management routes
      {
        path: ':customerID/settings/users',
        element: <CustomerUsersPage />,
      },
      {
        path: ':customerID/settings/users/:userID',
        element: <UserEditPage />,
      },
      {
        path: ':customerID/settings/users/:userID/groups',
        element: <UserGroupsPage />,
      },

      // Setting routes
      {
        path: ':customerID/settings/:setting',
        element: <SettingPage />,
      },
      {
        path: ':customerID/settings/:setting/:settingNo',
        element: <SettingPage />,
      },

      // Distributor tools routes
      {
        path: ':customerID/distributor/transferDevices',
        element: <TransferDevicesPage />,
      },
      {
        path: ':customerID/distributor/newCustomer',
        element: <NewCustomer />,
      },
      {
        path: ':customerID/distributor/existingCustomer',
        element: <ExistingCustomer />,
      },
      {
        path: ':customerID/distributor/existingCustomer/:selectedCustomer',
        element: <ExistingCustomer />,
      },

      // User management route
      {
        path: ':customerID/users',
        element: <CustomerUsersPage />,
      },

      // Fallback route for any unmatched protected paths
      {
        path: '*',
        element: <Home />,
      },
    ],
  },
]

// Create the router with conditional basename
const router = createBrowserRouter(routes, {
  basename: '/',
  future: {
    v7_startTransition: true,
  },
})

export default router
